import React from 'react';
import { Box, Container, Typography, Card, CardContent, Divider, Stack, Alert } from '@mui/material';
import { Security, Lock, Shield, VerifiedUser } from '@mui/icons-material';
import PublicNavbar from '../components/common/PublicNavbar';
import PublicFooter from '../components/common/PublicFooter';

export default function PrivacyPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#060d17', color: '#f8fafc' }}>
      <PublicNavbar />

      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Security sx={{ color: '#34d399', fontSize: 32 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Effective Date: January 1, 2026 • Version 1.0
            </Typography>
          </Box>

          <Card
            sx={{
              bgcolor: 'rgba(15, 23, 42, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 4,
              p: { xs: 2, sm: 4 },
              lineHeight: 1.8,
            }}
          >
            <CardContent>
              <Stack spacing={4}>
                {/* 1. Introduction */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    1. Introduction
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    AlphaTrading Alerts (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is deeply committed to protecting your personal information and privacy. This Privacy Policy explains what data we collect when you register, subscribe, and connect your Telegram account to our trading notification bot service, how that data is used, and how we keep it secure.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 2. Information We Collect */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    2. Information We Collect
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    We collect only the information necessary to provide, manage, and verify your monthly subscription:
                  </Typography>
                  <Box component="ul" sx={{ color: '#cbd5e1', pl: 3, mt: 1, '& li': { mb: 1 } }}>
                    <li>
                      <strong>Registration Profile Data:</strong> Full Name, Mobile Phone Number, and Email Address.
                    </li>
                    <li>
                      <strong>Security Credentials:</strong> Passwords are cryptographically salted and hashed using bcrypt. Plaintext passwords are never recorded, viewed, or stored in our database.
                    </li>
                    <li>
                      <strong>Compliance & Audit Trails:</strong> Timestamp of Terms and Privacy acceptance, policy versions accepted, and User IP Address recorded at registration.
                    </li>
                    <li>
                      <strong>Payment Records:</strong> Razorpay transaction identifiers, payment status, paid timestamp, and amount. We <em>never</em> store credit/debit card numbers, CVVs, or UPI PINs. All payment transactions are securely handled directly by Razorpay (PCI-DSS Level 1 compliant).
                    </li>
                    <li>
                      <strong>Telegram Connection Data:</strong> Telegram Chat ID, Telegram User ID, username, and token linking metadata required to deliver signals exclusively to your chat.
                    </li>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 3. How We Use Information */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    3. How We Use Your Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Your personal information is used exclusively for:
                  </Typography>
                  <Box component="ul" sx={{ color: '#cbd5e1', pl: 3, mt: 1, '& li': { mb: 0.5 } }}>
                    <li>Activating and administering your ₹1,000 monthly bot subscription.</li>
                    <li>Sending real-time trading notifications to your linked Telegram account.</li>
                    <li>Sending payment confirmation receipts and renewal reminders.</li>
                    <li>Preventing fraud, unauthorized account sharing, and piracy.</li>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 4. Information Sharing & Third Parties */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    4. Data Sharing & Third-Party Disclosure
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    <strong>We do not sell, rent, lease, or trade your personal data with third-party advertisers or marketing agencies.</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    Information is shared strictly with trusted operational infrastructure partners:
                  </Typography>
                  <Box component="ul" sx={{ color: '#cbd5e1', pl: 3, mt: 1, '& li': { mb: 0.5 } }}>
                    <li><strong>Razorpay:</strong> To process secure payment transactions and verify payment callbacks.</li>
                    <li><strong>Telegram Bot API:</strong> To deliver real-time messages and notifications to your authorized chat.</li>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 5. Data Security */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    5. Data Security & Storage
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    We employ industry-standard security protocols, including 256-bit Secure Sockets Layer (SSL) encryption, firewalled databases, and restricted access control.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 6. Contact Us */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    6. Privacy Inquiries & Contact
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    If you have questions regarding this Privacy Policy or wish to update or delete your profile data, please contact our support team via Telegram at <strong>@JigendraTradingAlerts_bot</strong>.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
}
